import React, {useEffect, useState} from 'react';
import {
    Form, 
    Input, 
    Button, 
    Card, 
    Row, 
    Col, 
    InputNumber, 
    Switch, 
    Select, 
    Space, 
    Divider,
    message,
    Table,
    Popconfirm
} from 'antd';
import {PlusOutlined, DeleteOutlined, SaveOutlined, ArrowLeftOutlined} from '@ant-design/icons';
import {useApi} from '../provider/ApiProvider';
import {useHistory, useParams} from 'react-router-dom';
import LayoutComponent from '../components/LayoutComponent';

const {Option} = Select;

const TargetLocationSavePage = () => {
    const {api} = useApi();
    const history = useHistory();
    const {id} = useParams();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [doors, setDoors] = useState([]);
    const [isEdit, setIsEdit] = useState(false);

    useEffect(() => {
        if (id) {
            setIsEdit(true);
            fetchTargetLocation();
        }
    }, [id]);

    const fetchTargetLocation = async () => {
        setLoading(true);
        try {
            const response = await api.get(`/targetLocationRouter/get/${id}`);
            if (response.status) {
                const data = response.data;
                form.setFieldsValue({
                    name: data.name,
                    latitude: data.latitude,
                    longitude: data.longitude,
                    radius: data.radius,
                    color: data.color,
                    fillColor: data.fillColor,
                    strokeColor: data.strokeColor,
                    login: data.login,
                    isActive: data.isActive
                });
                setDoors(data.doors || []);
            }
        } catch (error) {
            message.error('Hedef lokasyon yüklenirken hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    const handleSubmit = async (values) => {
        setLoading(true);
        try {
            const submitData = {
                ...values,
                doors: doors
            };

            let response;
            if (isEdit) {
                response = await api.put(`/targetLocationRouter/update/${id}`, submitData);
            } else {
                response = await api.post('/targetLocationRouter/add', submitData);
            }

            if (response.status) {
                message.success(`Hedef lokasyon başarıyla ${isEdit ? 'güncellendi' : 'oluşturuldu'}`);
                history.push('/target-locations');
            }
        } catch (error) {
            message.error(`Hedef lokasyon ${isEdit ? 'güncellenirken' : 'oluşturulurken'} hata oluştu`);
        } finally {
            setLoading(false);
        }
    };

    const addDoor = () => {
        const newDoor = {
            id: Date.now(), // Temporary ID for frontend
            name: '',
            qr: '',
            type: 'entrance',
            doorNumber: doors.length + 1,
            isActive: true
        };
        setDoors([...doors, newDoor]);
    };

    const removeDoor = (doorId) => {
        setDoors(doors.filter(door => door.id !== doorId));
    };

    const updateDoor = (doorId, field, value) => {
        setDoors(doors.map(door => 
            door.id === doorId ? {...door, [field]: value} : door
        ));
    };

    const doorColumns = [
        {
            title: 'Kapı No',
            dataIndex: 'doorNumber',
            key: 'doorNumber',
            width: 100,
            render: (_, record) => (
                <InputNumber
                    value={record.doorNumber}
                    onChange={(value) => updateDoor(record.id, 'doorNumber', value)}
                    min={1}
                    style={{width: 80}}
                />
            )
        },
        {
            title: 'Ad',
            dataIndex: 'name',
            key: 'name',
            render: (_, record) => (
                <Input
                    value={record.name}
                    onChange={(e) => updateDoor(record.id, 'name', e.target.value)}
                    placeholder="Kapı adı"
                />
            )
        },
        {
            title: 'QR Kodu',
            dataIndex: 'qr',
            key: 'qr',
            render: (_, record) => (
                <Input
                    value={record.qr}
                    onChange={(e) => updateDoor(record.id, 'qr', e.target.value)}
                    placeholder="QR kod"
                />
            )
        },
        {
            title: 'Tip',
            dataIndex: 'type',
            key: 'type',
            width: 120,
            render: (_, record) => (
                <Select
                    value={record.type}
                    onChange={(value) => updateDoor(record.id, 'type', value)}
                    style={{width: '100%'}}
                >
                    <Option value="entrance">Giriş</Option>
                    <Option value="exit">Çıkış</Option>
                </Select>
            )
        },
        {
            title: 'Durum',
            dataIndex: 'isActive',
            key: 'isActive',
            width: 100,
            render: (_, record) => (
                <Switch
                    checked={record.isActive}
                    onChange={(checked) => updateDoor(record.id, 'isActive', checked)}
                />
            )
        },
        {
            title: 'İşlem',
            key: 'action',
            width: 100,
            render: (_, record) => (
                <Popconfirm
                    title="Bu kapıyı silmek istediğinizden emin misiniz?"
                    onConfirm={() => removeDoor(record.id)}
                    okText="Evet"
                    cancelText="Hayır"
                >
                    <Button 
                        type="primary" 
                        danger 
                        icon={<DeleteOutlined />} 
                        size="small"
                    />
                </Popconfirm>
            )
        }
    ];

    return (
        <LayoutComponent>
            <div style={{ padding: '24px' }}>
                <Card 
                    title={`Hedef Lokasyon ${isEdit ? 'Düzenle' : 'Ekle'}`}
                    extra={
                        <Button 
                            icon={<ArrowLeftOutlined />} 
                            onClick={() => history.push('/target-locations')}
                        >
                            Geri Dön
                        </Button>
                    }
                >
                    <Form
                        form={form}
                        layout="vertical"
                        onFinish={handleSubmit}
                        initialValues={{
                            radius: 1000,
                            color: '#FF6B6B',
                            fillColor: 'rgba(255, 107, 107, 0.2)',
                            strokeColor: 'rgba(255, 107, 107, 0.8)',
                            login: 1,
                            isActive: true
                        }}
                    >
                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="name"
                                    label="Lokasyon Adı"
                                    rules={[{required: true, message: 'Lokasyon adı gerekli!'}]}
                                >
                                    <Input placeholder="Lokasyon adını girin" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="login"
                                    label="Giriş Tipi"
                                    rules={[{required: true, message: 'Giriş tipi gerekli!'}]}
                                >
                                    <Select placeholder="Giriş tipini seçin">
                                        <Option value={1}>Tek Giriş</Option>
                                        <Option value={2}>Çoklu Giriş</Option>
                                    </Select>
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="latitude"
                                    label="Enlem (Latitude)"
                                    rules={[{required: true, message: 'Enlem gerekli!'}]}
                                >
                                    <InputNumber
                                        placeholder="41.36132950323434"
                                        style={{width: '100%'}}
                                        step={0.00000000000001}
                                        precision={14}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="longitude"
                                    label="Boylam (Longitude)"
                                    rules={[{required: true, message: 'Boylam gerekli!'}]}
                                >
                                    <InputNumber
                                        placeholder="36.55000000000000"
                                        style={{width: '100%'}}
                                        step={0.00000000000001}
                                        precision={14}
                                    />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="radius"
                                    label="Yarıçap (metre)"
                                    rules={[{required: true, message: 'Yarıçap gerekli!'}]}
                                >
                                    <InputNumber
                                        placeholder="1000"
                                        style={{width: '100%'}}
                                        min={1}
                                    />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="color"
                                    label="Ana Renk"
                                    rules={[{required: true, message: 'Ana renk gerekli!'}]}
                                >
                                    <Input placeholder="#FF6B6B" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="fillColor"
                                    label="Dolgu Rengi"
                                    rules={[{required: true, message: 'Dolgu rengi gerekli!'}]}
                                >
                                    <Input placeholder="rgba(255, 107, 107, 0.2)" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="strokeColor"
                                    label="Çizgi Rengi"
                                    rules={[{required: true, message: 'Çizgi rengi gerekli!'}]}
                                >
                                    <Input placeholder="rgba(255, 107, 107, 0.8)" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Form.Item
                            name="isActive"
                            label="Aktif"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>

                        <Divider>Kapılar</Divider>

                        <div style={{ marginBottom: 16 }}>
                            <Button 
                                type="dashed" 
                                icon={<PlusOutlined />} 
                                onClick={addDoor}
                            >
                                Kapı Ekle
                            </Button>
                        </div>

                        <Table
                            columns={doorColumns}
                            dataSource={doors}
                            rowKey="id"
                            pagination={false}
                            size="small"
                        />

                        <Divider />

                        <Form.Item>
                            <Space>
                                <Button 
                                    type="primary" 
                                    htmlType="submit" 
                                    icon={<SaveOutlined />}
                                    loading={loading}
                                >
                                    {isEdit ? 'Güncelle' : 'Kaydet'}
                                </Button>
                                <Button onClick={() => history.push('/target-locations')}>
                                    İptal
                                </Button>
                            </Space>
                        </Form.Item>
                    </Form>
                </Card>
            </div>
        </LayoutComponent>
    );
};

export default TargetLocationSavePage;
